// frontend/src/components/AdminHeader.js
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';

/**
 * Shared header component for all admin pages
 * Provides consistent navigation across the admin section
 * 
 * @param {string} title - Page title
 * @param {string} subtitle - Page subtitle/description
 * @param {string} currentPage - Current page identifier ('events', 'players', 'settings', 'dashboard', 'team')
 */
const AdminHeader = ({ currentPage }) => {
    const { role, logout } = useAuth();
    const navigate = useNavigate();

    // Keyboard nav: press 'g' then 'e' / 'p' / 's' (within 1.5s) to jump.
    // Skips when user is typing in an input/textarea/contentEditable, or holding modifier keys.
    useEffect(() => {
        let gPressed = false;
        let timeoutId = null;

        const handler = (e) => {
            const tag = e.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;

            const key = e.key.toLowerCase();

            if (gPressed) {
                gPressed = false;
                if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }

                if (key === 'e') { e.preventDefault(); navigate('/admin/events'); }
                else if (key === 'p' && role !== 'event_admin') { e.preventDefault(); navigate('/admin/players'); }
                else if (key === 's' && role === 'super_admin') { e.preventDefault(); navigate('/admin/settings'); }
            } else if (key === 'g') {
                gPressed = true;
                if (timeoutId) clearTimeout(timeoutId);
                timeoutId = setTimeout(() => { gPressed = false; timeoutId = null; }, 1500);
            }
        };

        window.addEventListener('keydown', handler);
        return () => {
            window.removeEventListener('keydown', handler);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [navigate, role]);

    // Style helper for nav items - active items get a solid white background
    const getNavItemClass = (page) => {
        const baseClasses = "px-3 sm:px-4 py-2 rounded-xl transition-all duration-300 flex items-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-wider";
        if (currentPage === page) {
            return `${baseClasses} bg-white text-mjwc-primary shadow-lg scale-105`;
        }
        return `${baseClasses} text-white/70 hover:text-white hover:bg-white/10`;
    };

    return (
        <header className="bg-mjwc-primary/95 backdrop-blur-xl sticky top-0 z-50 border-b border-white/10 shadow-2xl transition-all duration-500">
            <div className="max-w-7xl mx-auto px-6 py-3">
                <div className="flex justify-between items-center">
                    {/* Left: Identity/Logo */}
                    <div
                        className="flex items-center gap-3 group cursor-pointer"
                        onClick={() => navigate('/admin/events')}
                    >
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-400 to-cyan-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <svg className="w-5 h-5 text-mjwc-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <circle cx="12" cy="13" r="3" strokeWidth={3} />
                            </svg>
                        </div>
                        <div className="hidden sm:block">
                            <span className="text-white text-xs font-black uppercase tracking-[0.3em] block leading-none">Admin</span>
                            <span className="text-emerald-400 text-[9px] font-bold uppercase tracking-[0.2em] block mt-1">Console</span>
                        </div>
                    </div>

                    {/* Right: Navigation */}
                    <nav className="flex items-center gap-1.5 sm:gap-4">
                        <Link to="/admin/events" className={getNavItemClass('events')}>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="hidden md:inline">Events</span>
                        </Link>

                        {/* Global (cross-event) player directory is not available to event_admins. */}
                        {role !== 'event_admin' && (
                            <Link to="/admin/players" className={getNavItemClass('players')}>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                                <span className="hidden md:inline">Players</span>
                            </Link>
                        )}

                        {role === 'super_admin' && (
                            <Link to="/admin/team-managers" className={getNavItemClass('team-managers')}>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M11.5 3a17 17 0 000 18M12.5 3a17 17 0 010 18" />
                                </svg>
                                <span className="hidden md:inline">Team Managers</span>
                            </Link>
                        )}

                        {role === 'super_admin' && (
                            <Link to="/admin/settings" className={getNavItemClass('settings')}>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <circle cx="12" cy="12" r="3" strokeWidth={2.5} />
                                </svg>
                                <span className="hidden md:inline">Settings</span>
                            </Link>
                        )}

                        <div className="w-px h-6 bg-white/10 mx-2" />

                        <button
                            onClick={logout}
                            className="p-2 hover:bg-white/10 rounded-xl transition-colors text-white/70 hover:text-white"
                            title="Logout"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                        </button>
                    </nav>
                </div>
            </div>
        </header>
    );
};

export default AdminHeader;
